import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { EMPTY_COVERAGE, type CompanyValues, type CoverageValues, type GeneralInfo, type GovData, type Vehicle } from './types'

interface Session {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  general: GeneralInfo
  vehicles: Vehicle[]
  nextVehicleId: number
  nextDocNumber?: number
  savedSearchId?: number | null
}

interface HomeStore {
  // multi-session storage
  sessions: Record<string, Session>
  sessionOrder: string[]
  currentSessionId: string

  // bumped on reset; used by components to force-clean transient UI
  resetVersion: number

  // mirrors of the current session (for component access)
  general: GeneralInfo
  vehicles: Vehicle[]
  nextVehicleId: number

  // general info
  setPojistnik: (c: CompanyValues | null) => void
  setVlastnik: (c: CompanyValues | null) => void
  setProvozovatel: (c: CompanyValues | null) => void
  setOwnerSameAsPolicyholder: (v: boolean) => void
  setOperatorSameAsPolicyholder: (v: boolean) => void
  setGeneralField: <K extends keyof GeneralInfo>(key: K, value: GeneralInfo[K]) => void
  setGlobalCoverage: (c: CoverageValues) => void

  // vehicle operations
  addVehicle: (input: { vin: string }) => number | null
  addVehicles: (inputs: { vin: string }[]) => number[]
  allocDocNumber: () => number
  removeVehicle: (id: number) => void
  updateVehicle: (id: number, patch: Partial<Vehicle>) => void
  setVehicleGovData: (id: number, gov: GovData | null, notFound: boolean) => void
  setVehicleInsurance: (id: number, coverage: CoverageValues | null) => void
  clearVehicleInsurance: (id: number) => void
  hasVin: (vin: string) => boolean

  // session management
  createSession: (name?: string) => string
  switchSession: (id: string) => void
  renameSession: (id: string, name: string) => void
  deleteSession: (id: string) => void
  duplicateSession: (id: string) => string
  importSession: (
    name: string,
    snapshot: { general: GeneralInfo; vehicles: Vehicle[]; nextVehicleId?: number },
    savedSearchId?: number,
  ) => string
  findSessionBySavedId: (savedSearchId: number) => string | null
  setSavedSearchId: (sessionId: string, savedSearchId: number | null) => void

  // reset
  resetAll: () => void
}

function todayIso(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function newId(): string {
  return `s_${Math.random().toString(36).slice(2, 10)}`
}

function makeInitialGeneral(): GeneralInfo {
  return {
    pojistnik: {},
    vat: false,
    vlastnik: {},
    provozovatel: {},
    ownerSameAsPolicyholder: true,
    operatorSameAsPolicyholder: true,
    insurance_start_date: todayIso(),
    uziti_vozidla: 'Běžné užití',
    dalsi_informace: '',
    pojistne_kryti: { ...EMPTY_COVERAGE },
  }
}

function makeSession(name?: string): Session {
  const now = new Date().toISOString()
  return {
    id: newId(),
    name: name ?? `Vyhledávání ${new Date().toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })}`,
    createdAt: now,
    updatedAt: now,
    general: makeInitialGeneral(),
    vehicles: [],
    nextVehicleId: 1,
    nextDocNumber: 1,
  }
}

function mirrorOf(s: Session) {
  return { general: s.general, vehicles: s.vehicles, nextVehicleId: s.nextVehicleId }
}

const initialSession = makeSession('Výchozí')
const initialSessions: Record<string, Session> = { [initialSession.id]: initialSession }

export const useHomeStore = create<HomeStore>()(
  persist(
    (set, get) => {
      function patchCurrent(fn: (s: Session) => Partial<Session>): void {
        set((state) => {
          const cur = state.sessions[state.currentSessionId]
          if (!cur) return state
          const updated: Session = { ...cur, ...fn(cur), updatedAt: new Date().toISOString() }
          return {
            sessions: { ...state.sessions, [updated.id]: updated },
            ...mirrorOf(updated),
          }
        })
      }

      return {
        sessions: initialSessions,
        sessionOrder: [initialSession.id],
        currentSessionId: initialSession.id,
        resetVersion: 0,

        general: initialSession.general,
        vehicles: initialSession.vehicles,
        nextVehicleId: initialSession.nextVehicleId,

        setPojistnik: (c) =>
          patchCurrent((s) => ({
            general: {
              ...s.general,
              pojistnik: c ?? {},
              // Auto-detect VAT payer from registry: a company that has a DIČ
              // (COMPANY_VAT_NUMBER) is registered for DPH. Only re-derive on a
              // real selection (object); ignore the transient null while typing.
              vat: c ? !!c.COMPANY_VAT_NUMBER : s.general.vat,
              vlastnik: s.general.ownerSameAsPolicyholder ? c ?? {} : s.general.vlastnik,
              provozovatel: s.general.operatorSameAsPolicyholder ? c ?? {} : s.general.provozovatel,
            },
          })),

        setVlastnik: (c) =>
          patchCurrent((s) => ({
            general: { ...s.general, vlastnik: c ?? {} },
            // Propagate the global owner to vehicles that follow the global setting.
            vehicles: s.general.ownerSameAsPolicyholder
              ? s.vehicles
              : s.vehicles.map((v) =>
                  v.ownerOverridden
                    ? v
                    : { ...v, ownerSameAsPolicyholder: false, owner_company: (c ?? {}) as CompanyValues },
                ),
          })),

        setProvozovatel: (c) =>
          patchCurrent((s) => ({
            general: { ...s.general, provozovatel: c ?? {} },
            vehicles: s.general.operatorSameAsPolicyholder
              ? s.vehicles
              : s.vehicles.map((v) =>
                  v.operatorOverridden
                    ? v
                    : { ...v, operatorSameAsPolicyholder: false, operator_company: (c ?? {}) as CompanyValues },
                ),
          })),

        setOwnerSameAsPolicyholder: (v) =>
          patchCurrent((s) => ({
            general: {
              ...s.general,
              ownerSameAsPolicyholder: v,
              // When unchecking "same as policyholder" the owner must be picked
              // fresh, so clear the field instead of leaving the policyholder in it.
              vlastnik: v ? s.general.pojistnik : {},
            },
            // Mirror the choice onto vehicles that still follow the global setting.
            vehicles: s.vehicles.map((ve) =>
              ve.ownerOverridden ? ve : { ...ve, ownerSameAsPolicyholder: v, owner_company: v ? null : {} },
            ),
          })),

        setOperatorSameAsPolicyholder: (v) =>
          patchCurrent((s) => ({
            general: {
              ...s.general,
              operatorSameAsPolicyholder: v,
              provozovatel: v ? s.general.pojistnik : {},
            },
            vehicles: s.vehicles.map((ve) =>
              ve.operatorOverridden ? ve : { ...ve, operatorSameAsPolicyholder: v, operator_company: v ? null : {} },
            ),
          })),

        setGeneralField: (key, value) =>
          patchCurrent((s) => ({ general: { ...s.general, [key]: value } })),

        setGlobalCoverage: (c) =>
          patchCurrent((s) => ({ general: { ...s.general, pojistne_kryti: c } })),

        addVehicle: ({ vin }) => {
          const v = vin.trim().toUpperCase()
          if (!v) return null
          if (get().vehicles.some((x) => x.vin === v)) return null
          let id = 0
          patchCurrent((s) => {
            id = s.nextVehicleId
            // New vehicles inherit the global owner/operator choice so the cards
            // match what was set in "Informace o klientovi" (and the XLS export).
            const ownerSame = s.general.ownerSameAsPolicyholder
            const operatorSame = s.general.operatorSameAsPolicyholder
            return {
              nextVehicleId: id + 1,
              vehicles: [
                ...s.vehicles,
                {
                  id,
                  vin: v,
                  insurance_start_date: s.general.insurance_start_date,
                  uziti_vozidla: s.general.uziti_vozidla,
                  insurance: null,
                  car_gov_data: null,
                  ownerSameAsPolicyholder: ownerSame,
                  operatorSameAsPolicyholder: operatorSame,
                  owner_company: ownerSame ? null : (s.general.vlastnik as CompanyValues),
                  operator_company: operatorSame ? null : (s.general.provozovatel as CompanyValues),
                },
              ],
            }
          })
          return id
        },

        addVehicles: (inputs) => {
          const ids: number[] = []
          for (const i of inputs) {
            const id = get().addVehicle(i)
            if (id !== null) ids.push(id)
          }
          return ids
        },

        allocDocNumber: () => {
          let n = 1
          patchCurrent((s) => {
            n = s.nextDocNumber ?? 1
            return { nextDocNumber: n + 1 }
          })
          return n
        },

        removeVehicle: (id) =>
          patchCurrent((s) => ({ vehicles: s.vehicles.filter((v) => v.id !== id) })),

        updateVehicle: (id, patch) =>
          patchCurrent((s) => ({
            vehicles: s.vehicles.map((v) => (v.id === id ? { ...v, ...patch } : v)),
          })),

        setVehicleGovData: (id, gov, notFound) =>
          patchCurrent((s) => ({
            vehicles: s.vehicles.map((v) =>
              v.id === id ? { ...v, car_gov_data: gov, registry_not_found: notFound } : v,
            ),
          })),

        setVehicleInsurance: (id, coverage) =>
          patchCurrent((s) => ({
            vehicles: s.vehicles.map((v) => (v.id === id ? { ...v, insurance: coverage } : v)),
          })),

        clearVehicleInsurance: (id) =>
          patchCurrent((s) => ({
            vehicles: s.vehicles.map((v) => (v.id === id ? { ...v, insurance: null } : v)),
          })),

        hasVin: (vin) => get().vehicles.some((v) => v.vin === vin.trim().toUpperCase()),

        createSession: (name) => {
          const s = makeSession(name)
          set((state) => ({
            sessions: { ...state.sessions, [s.id]: s },
            sessionOrder: [...state.sessionOrder, s.id],
            currentSessionId: s.id,
            ...mirrorOf(s),
          }))
          return s.id
        },

        switchSession: (id) =>
          set((state) => {
            const s = state.sessions[id]
            if (!s) return state
            return { currentSessionId: id, ...mirrorOf(s) }
          }),

        renameSession: (id, name) =>
          set((state) => {
            const s = state.sessions[id]
            if (!s) return state
            const updated = { ...s, name, updatedAt: new Date().toISOString() }
            return { sessions: { ...state.sessions, [id]: updated } }
          }),

        deleteSession: (id) =>
          set((state) => {
            if (!state.sessions[id]) return state
            const order = state.sessionOrder.filter((x) => x !== id)
            const { [id]: _removed, ...rest } = state.sessions
            void _removed
            // ensure at least one session exists
            if (order.length === 0) {
              const fresh = makeSession('Výchozí')
              return {
                sessions: { [fresh.id]: fresh },
                sessionOrder: [fresh.id],
                currentSessionId: fresh.id,
                ...mirrorOf(fresh),
              }
            }
            const nextCurrent = state.currentSessionId === id ? order[0] : state.currentSessionId
            const cur = rest[nextCurrent]
            return {
              sessions: rest,
              sessionOrder: order,
              currentSessionId: nextCurrent,
              ...(cur ? mirrorOf(cur) : {}),
            }
          }),

        importSession: (name, snapshot, savedSearchId) => {
          const now = new Date().toISOString()
          const s: Session = {
            id: newId(),
            name,
            createdAt: now,
            updatedAt: now,
            general: snapshot.general ?? makeInitialGeneral(),
            vehicles: snapshot.vehicles ?? [],
            nextVehicleId:
              snapshot.nextVehicleId ??
              (snapshot.vehicles && snapshot.vehicles.length > 0
                ? Math.max(...snapshot.vehicles.map((v) => v.id)) + 1
                : 1),
            savedSearchId: savedSearchId ?? null,
          }
          set((state) => ({
            sessions: { ...state.sessions, [s.id]: s },
            sessionOrder: [...state.sessionOrder, s.id],
            currentSessionId: s.id,
            ...mirrorOf(s),
          }))
          return s.id
        },

        findSessionBySavedId: (savedSearchId) => {
          const sessions = get().sessions
          for (const id of Object.keys(sessions)) {
            if (sessions[id].savedSearchId === savedSearchId) return id
          }
          return null
        },

        setSavedSearchId: (sessionId, savedSearchId) =>
          set((state) => {
            const s = state.sessions[sessionId]
            if (!s) return state
            return { sessions: { ...state.sessions, [sessionId]: { ...s, savedSearchId } } }
          }),

        duplicateSession: (id) => {
          const src = get().sessions[id]
          if (!src) return id
          const now = new Date().toISOString()
          const copy: Session = {
            ...src,
            id: newId(),
            name: `${src.name} – kopie`,
            createdAt: now,
            updatedAt: now,
          }
          set((state) => ({
            sessions: { ...state.sessions, [copy.id]: copy },
            sessionOrder: [...state.sessionOrder, copy.id],
            currentSessionId: copy.id,
            ...mirrorOf(copy),
          }))
          return copy.id
        },

        resetAll: () => {
          patchCurrent(() => ({
            general: makeInitialGeneral(),
            vehicles: [],
            nextVehicleId: 1,
            nextDocNumber: 1,
            savedSearchId: null,
          }))
          set((state) => ({ resetVersion: state.resetVersion + 1 }))
        },
      }
    },
    {
      name: 'fleeto-home',
      storage: createJSONStorage(() => localStorage),
      version: 2,
      migrate: (persisted: unknown, fromVersion: number) => {
        type LegacyV1 = { general?: GeneralInfo; vehicles?: Vehicle[]; nextVehicleId?: number }
        if (fromVersion < 2 && persisted && typeof persisted === 'object') {
          const legacy = persisted as LegacyV1
          const now = new Date().toISOString()
          const id = newId()
          const session: Session = {
            id,
            name: 'Výchozí',
            createdAt: now,
            updatedAt: now,
            general: legacy.general ?? makeInitialGeneral(),
            vehicles: legacy.vehicles ?? [],
            nextVehicleId: legacy.nextVehicleId ?? 1,
          }
          return {
            sessions: { [id]: session },
            sessionOrder: [id],
            currentSessionId: id,
            general: session.general,
            vehicles: session.vehicles,
            nextVehicleId: session.nextVehicleId,
          }
        }
        return persisted as object
      },
      partialize: (state) => ({
        sessions: state.sessions,
        sessionOrder: state.sessionOrder,
        currentSessionId: state.currentSessionId,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state) return
        const cur = state.sessions[state.currentSessionId]
        if (cur) {
          state.general = cur.general
          state.vehicles = cur.vehicles
          state.nextVehicleId = cur.nextVehicleId
        }
      },
    },
  ),
)
